import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Schedule() {
  const [templates, setTemplates] = useState([]);
  const [team, setTeam] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const todayIdx = (new Date().getDay() + 6) % 7;

  const load = useCallback(async () => {
    try {
      const [t, m] = await Promise.all([api.get("/templates"), api.get("/team")]);
      setTemplates(t.data);
      setTeam(m.data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load schedule"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (t) => setEdit({
    id: t.id,
    name: t.name,
    role: t.role,
    days: [...(t.schedule_days || [])],
    staff: [...(t.scheduled_user_ids || [])],
  });

  const roleStaff = useMemo(
    () => (edit ? team.filter((m) => m.primary_role === edit.role) : []),
    [team, edit]
  );

  const toggleDay = (d) => setEdit((e) => ({ ...e, days: e.days.includes(d) ? e.days.filter((x) => x !== d) : [...e.days, d] }));
  const toggleStaff = (id) => setEdit((e) => ({ ...e, staff: e.staff.includes(id) ? e.staff.filter((x) => x !== id) : [...e.staff, id] }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/templates/${edit.id}/schedule`, { schedule_days: edit.days, scheduled_user_ids: edit.staff });
      toast.success(`Schedule saved for "${edit.name}"`);
      setEdit(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not save schedule"));
    } finally {
      setBusy(false);
    }
  };

  const anyDay = templates.filter((t) => !(t.schedule_days || []).length);
  const byDay = (d) => templates.filter((t) => (t.schedule_days || []).includes(d));

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8" data-testid="schedule-page">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D20]">Weekly Planner</h1>
      <p className="text-sm text-slate-500 mt-1">
        Pick which days each template runs and who's scheduled. Templates with no days stay available every day; templates with assigned staff can only be started by them.
      </p>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
        {DAY_NAMES.map((name, d) => (
          <section
            key={name}
            data-testid={`schedule-day-column-${d}`}
            className={`rounded-2xl border p-3 min-h-[120px] ${d === todayIdx ? "bg-[#FDF0EB] border-terra-400/50" : "bg-white border-[#E4DDD3]"}`}
          >
            <div className={`text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${d === todayIdx ? "text-terra-600" : "text-slate-500"}`}>
              <CalendarDays className="w-3.5 h-3.5" /> {name}{d === todayIdx ? " · Today" : ""}
            </div>
            <div className="space-y-2">
              {byDay(d).length === 0 && <div className="text-xs text-slate-400">—</div>}
              {byDay(d).map((t) => (
                <TemplateChip key={t.id} t={t} onClick={() => openEdit(t)} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <section className="mt-8" data-testid="schedule-anyday-section">
        <h2 className="text-base md:text-lg font-semibold text-slate-700 uppercase tracking-wider mb-3">Flexible — Any Day</h2>
        {anyDay.length === 0 ? (
          <div className="p-5 rounded-2xl bg-white border border-[#E4DDD3] text-slate-500 text-sm" data-testid="schedule-no-flexible-message">
            Every template is scheduled to specific days.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {anyDay.map((t) => (
              <TemplateChip key={t.id} t={t} onClick={() => openEdit(t)} />
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="bg-white border-[#E4DDD3]" data-testid="schedule-edit-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[#1A1D20] font-display">Schedule — {edit?.name}</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-5">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Days it runs (none = every day)</div>
                <div className="flex gap-1.5 flex-wrap">
                  {DAY_NAMES.map((name, d) => (
                    <button
                      key={name}
                      onClick={() => toggleDay(d)}
                      data-testid={`schedule-day-toggle-${d}`}
                      className={`h-10 px-3 rounded-xl border text-sm font-medium transition-colors ${edit.days.includes(d) ? "bg-terra-500 text-white border-terra-400" : "bg-white border-[#E4DDD3] text-slate-600 hover:border-terra-400/50"}`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> Scheduled staff (none = any {edit.role})
                </div>
                {roleStaff.length === 0 ? (
                  <div className="text-sm text-slate-500" data-testid="schedule-no-staff-message">No {edit.role} staff on the team yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {roleStaff.map((m) => (
                      <label key={m.id} className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer select-none" data-testid={`schedule-staff-toggle-${m.id}`}>
                        <input
                          type="checkbox"
                          checked={edit.staff.includes(m.id)}
                          onChange={() => toggleStaff(m.id)}
                          className="w-4 h-4 accent-terra-500"
                        />
                        {m.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={save}
                disabled={busy}
                data-testid="schedule-save-button"
                className="w-full h-12 rounded-xl bg-terra-500 text-white font-display font-bold hover:bg-terra-600 disabled:opacity-50 transition-colors"
              >
                {busy ? "Saving…" : "Save Schedule"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}

function TemplateChip({ t, onClick }) {
  const names = t.scheduled_user_names || [];
  return (
    <button
      onClick={onClick}
      data-testid={`schedule-template-button-${t.id}`}
      className="w-full text-left p-3 rounded-xl bg-white border border-[#E4DDD3] hover:border-terra-400/60 hover:bg-[#FDF0EB]/40 transition-colors"
    >
      <div className="text-sm font-medium text-[#1A1D20] leading-snug">{t.name}</div>
      <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{t.role}</div>
      {names.length > 0 && (
        <div className="text-xs text-terra-600 mt-1 flex items-center gap-1">
          <Users className="w-3 h-3" /> {names.map((n) => n.split(" ")[0]).join(", ")}
        </div>
      )}
    </button>
  );
}
